import React from 'react';

import { observer } from 'mobx-react';
import { IconButton, Switch } from '@infoods/lifebase-ui';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';

import classes from './FoodRowActions.module.scss';
import { GridTableCell } from '../../../../../shared/components/table-components';
import { SeparatorLine } from '../../../../../shared/components/ui-components';
import { openConfirmationModal } from '../../../../../shared/features/confirmation';
import { loggedInUserDataStore } from '../../../../../shared/stores/loggedInUserDataStore';
import { FoodType } from '../../../../constants/foodType';
import { FoodListItem } from '../../../../interfaces/FoodListItem';
import { useFoodsListStore } from '../../hooks/useFoodsListStore';

type FoodRowActionsProps = {
  food: FoodListItem;
  /** the list this row belongs to — ban/delete refetch that type's store */
  foodType: FoodType;
  /** `addedByUser || isSuperAdmin` — there is no `editable` field on the foods DTO */
  editable: boolean;
  onEditClick: () => void;
};

const FoodRowActions = observer(
  ({ food, foodType, editable, onEditClick }: FoodRowActionsProps) => {
    const { id, banned } = food;
    const { isSuperAdmin } = loggedInUserDataStore;
    const { changeFoodState, deleteFood } = useFoodsListStore(foodType);

    const handleBanToggle = () => {
      changeFoodState(id, !banned);
    };

    const handleDeleteClick = () => {
      openConfirmationModal({
        title: `Are you sure you want to delete ${food.name}?`,
        confirmBtnConfig: { onClick: () => deleteFood(id), text: 'Delete' },
      });
    };

    return (
      <GridTableCell className={classes.actionsCell}>
        {!isSuperAdmin && <Switch checked={!banned} onChange={handleBanToggle} />}
        <SeparatorLine />
        <IconButton
          onClick={onEditClick}
          color="secondary"
          data-test-id={editable ? 'edit-btn' : 'clone-btn'}
        >
          {editable ? (
            <ModeEditOutlinedIcon fontSize="medium" />
          ) : (
            <ContentCopyRoundedIcon fontSize="medium" />
          )}
        </IconButton>
        {editable && (
          <IconButton
            onClick={handleDeleteClick}
            color="secondary"
            data-test-id="delete-btn"
          >
            <DeleteOutlineRoundedIcon fontSize="medium" />
          </IconButton>
        )}
      </GridTableCell>
    );
  },
);

export { FoodRowActions };
